/**
 * The model pick behind an Explore chat and a Create → Q&A run — picklist
 * loading, the default choice, and the "connection · model" label. The picker
 * UI is `ChatModelPicker.tsx`.
 */

import { useCallback, useEffect, useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  listChatModels,
  type ChatConnectionOptionOut,
  type ChatOut,
  type PlatformChatModelOut,
} from "@/shared/api/ai";

export interface ChatModelChoice {
  connectionId: string | null;
  deployment: string | null;
}

/** The picker's value for the pinned platform deployment (Q&A only). It is
 *  never sent: a platform choice goes over the wire as no connection. */
export const PLATFORM_CHOICE_ID = "platform";

/** Load the picklist. `null` while loading; `[]` when there is nothing usable.
 *  `platform` is the pinned platform deployment, or null when none is set up. */
export function useChatModels(enabled: boolean): {
  options: ChatConnectionOptionOut[] | null;
  platform: PlatformChatModelOut | null;
  error: string | null;
} {
  const getToken = useAccessToken();
  const [options, setOptions] = useState<ChatConnectionOptionOut[] | null>(null);
  const [platform, setPlatform] = useState<PlatformChatModelOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const catalog = await listChatModels(token);
      setOptions(catalog.items);
      setPlatform(catalog.platform ?? null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the AI connections.");
      setOptions([]);
    }
  }, [getToken]);

  useEffect(() => {
    if (enabled) void load();
  }, [enabled, load]);

  return { options, platform, error };
}

/** The sensible default: the given choice if it is still offered, else the
 *  first connection and its first deployment. */
export function defaultChoice(
  options: ChatConnectionOptionOut[],
  current?: ChatModelChoice,
): ChatModelChoice {
  const conn = options.find((o) => o.connection_id === current?.connectionId) ?? options[0] ?? null;
  if (!conn) return { connectionId: null, deployment: null };
  const dep =
    conn.deployments.find((d) => d.name === current?.deployment) ?? conn.deployments[0] ?? null;
  return { connectionId: conn.connection_id, deployment: dep?.name ?? null };
}

/** As `defaultChoice`, with the platform model offered too: kept if it is the
 *  current choice, and the default when there is no usable connection. */
export function defaultChoiceWithPlatform(
  options: ChatConnectionOptionOut[],
  platform: PlatformChatModelOut | null,
  current?: ChatModelChoice,
): ChatModelChoice {
  const platformChoice = platform
    ? { connectionId: PLATFORM_CHOICE_ID, deployment: platform.deployment }
    : null;
  if (current?.connectionId === PLATFORM_CHOICE_ID && platformChoice) return platformChoice;
  const picked = defaultChoice(options, current);
  if (picked.connectionId) return picked;
  return platformChoice ?? picked;
}

/** The wire form of a choice: the platform model is no connection at all. */
export function choiceToRequest(choice: ChatModelChoice): {
  connection_id: string | null;
  deployment: string | null;
} {
  if (choice.connectionId === PLATFORM_CHOICE_ID) return { connection_id: null, deployment: null };
  return { connection_id: choice.connectionId, deployment: choice.deployment };
}

/** "Connection · model" for a chat card / header; "Platform model" for a chat
 *  created before the model pick existed. */
export function chatModelLabel(chat: ChatOut): string {
  if (chat.platform_model) return "Platform model";
  return `${chat.connection_name ?? "Connection removed"} · ${chat.deployment ?? ""}`;
}
